/**
 * Lift2Fit — built-in demo dataset.
 *
 * Used by lib/api.ts whenever the local API is unreachable, and by the demo role
 * switcher so every dashboard can be reviewed without a backend. It is not a
 * mock of the UI: it is a faithful instance of the contract in
 * docs/architecture/app-backend-contract.md, extended with the six training
 * tracks (strength · hypertrophy · conditioning · performance · wellness ·
 * general).
 *
 * SCOPE RULE (client direction): Lift2Fit is gym-based and indoor only. Every
 * movement here is an erg, sled, carry, plyometric, cable, machine, dumbbell,
 * barbell or mat exercise. No running, no outdoor/field work, no GPS, no weather.
 */
import type {
  AuthResponse,
  BlockHistoryEntry,
  BlockHistoryPayload,
  BlockTotals,
  BlockWeek,
  CurrentBlock,
  DashboardPayload,
  ExerciseOption,
  GeneratedPlan,
  Goal,
  GoalInput,
  GoalMetric,
  GoalPatch,
  Highlight,
  LifterDashboard,
  LifterSchedule,
  NextSession,
  PlanExercise,
  PlanIntake,
  RecentSet,
  RosterDashboard,
  ScheduleEntry,
  ScheduleInput,
  SessionSummary,
  StatsSummary,
  StreakSummary,
  TrackMetrics,
  Trainee,
  TrainingTrack,
  TrendPoint,
  User,
  Week,
  LogSetInput,
  LogSetResult,
  PersonalRecordEntry,
} from '../lib/api';
import { uid } from '../lib/format';

/* ---------------------------------------------------------------------------
   Tracks — the shared taxonomy
   ------------------------------------------------------------------------ */

export type TrackIconKey = 'strength' | 'hypertrophy' | 'conditioning' | 'performance' | 'wellness' | 'general';

export interface TrackMeta {
  id: TrainingTrack;
  /** the name on the wizard card */
  label: string;
  /** one-line description under the card title */
  description: string;
  /** the specialist coach identity, mirrors track_metrics.coach_persona */
  persona: string;
  /** the three headline numbers this track leads with */
  metricsFocus: [string, string, string];
  /** what one training slot is called for this track */
  sessionWord: string;
  /** indoor-safe call to action for empty states */
  emptyAction: string;
  /** indoor-safe upsell line for the coach card */
  coachPitch: string;
  icon: TrackIconKey;
}

export const TRACKS: TrackMeta[] = [
  {
    id: 'strength',
    label: 'Strength',
    description: 'Get genuinely stronger — e1RM, top-set load, tonnage.',
    persona: 'Strength Coach',
    metricsFocus: ['Volume', 'Best e1RM', 'Top set'],
    sessionWord: 'session',
    emptyAction: 'Log today’s session',
    coachPitch: 'Your Strength Coach reads every top set and writes the next block.',
    icon: 'strength',
  },
  {
    id: 'hypertrophy',
    label: 'Bodybuilding',
    description: 'Build muscle, deliberately — sets per muscle, volume, bodyweight.',
    persona: 'Hypertrophy Coach',
    metricsFocus: ['Volume', 'Sets per muscle', 'Bodyweight'],
    sessionWord: 'session',
    emptyAction: 'Log today’s session',
    coachPitch: 'Your Hypertrophy Coach balances weekly sets per muscle and progression.',
    icon: 'hypertrophy',
  },
  {
    id: 'conditioning',
    label: 'Conditioning',
    description: 'Engine work that fits your week — ergs, sleds, carries, circuits.',
    persona: 'Conditioning Coach',
    metricsFocus: ['Interval minutes', 'Work:rest', 'Erg metres'],
    sessionWord: 'interval session',
    emptyAction: 'Log your erg intervals',
    coachPitch: 'Your Conditioning Coach builds the intervals and holds your work:rest honest.',
    icon: 'conditioning',
  },
  {
    id: 'performance',
    label: 'Performance',
    description: 'Train for power and explosiveness — in-gym jumps, throws, plyos and sled accelerations.',
    persona: 'Athletic Performance Coach',
    metricsFocus: ['Power markers', 'Jump height', 'Readiness'],
    sessionWord: 'power session',
    emptyAction: 'Log today’s power session',
    coachPitch: 'Your Performance Coach tracks power markers and readiness before adding load.',
    icon: 'performance',
  },
  {
    id: 'wellness',
    label: 'Wellness & recovery',
    description: 'Mobility, stability, low-impact work — consistency and how you feel.',
    persona: 'Wellness & Recovery Coach',
    metricsFocus: ['Mobility sessions', 'Pain score', 'Consistency'],
    sessionWord: 'mobility session',
    emptyAction: 'Log a mobility session',
    coachPitch: 'Your Wellness & Recovery Coach keeps the plan gentle and the streak alive.',
    icon: 'wellness',
  },
  {
    id: 'general',
    label: 'General fitness',
    description: 'Balanced all-round training — volume, sessions per week, consistency.',
    persona: 'Performance Coach',
    metricsFocus: ['Volume', 'Sessions', 'Consistency'],
    sessionWord: 'session',
    emptyAction: 'Log today’s session',
    coachPitch: 'Your coach keeps the week balanced and the log honest.',
    icon: 'general',
  },
];

export const TRACK_BY_ID: Record<TrainingTrack, TrackMeta> = TRACKS.reduce(
  (acc, track) => {
    acc[track.id] = track;
    return acc;
  },
  {} as Record<TrainingTrack, TrackMeta>,
);

export function trackMeta(track: TrainingTrack | null | undefined): TrackMeta {
  return TRACK_BY_ID[track ?? 'general'] ?? TRACK_BY_ID.general;
}

/** Fallback for track_metrics.coach_persona when the API is unavailable. */
export function coachPersona(track: TrainingTrack | null | undefined): string {
  return trackMeta(track).persona;
}

/** The intake goal that best matches a track — used to prefill the wizard. */
export function defaultGoalForTrack(track: TrainingTrack): PlanIntake['goal'] {
  switch (track) {
    case 'strength':
      return 'strength';
    case 'hypertrophy':
      return 'hypertrophy';
    case 'conditioning':
      return 'endurance';
    case 'performance':
      return 'general';
    default:
      return 'general';
  }
}

/** Goal options offered in the wizard, with plain-English labels. */
export const GOAL_OPTIONS: Array<{ id: PlanIntake['goal']; label: string; description: string }> = [
  { id: 'strength', label: 'Get stronger', description: 'Heavier top sets and a higher e1RM.' },
  { id: 'hypertrophy', label: 'Build muscle', description: 'More weekly sets and a slow gain in bodyweight.' },
  { id: 'fat_loss', label: 'Get leaner', description: 'Keep the load, tighten the week.' },
  { id: 'endurance', label: 'Build the engine', description: 'Indoor intervals, ergs and circuits.' },
  { id: 'general', label: 'Feel better all round', description: 'Balanced training you can keep.' },
];

/* ---------------------------------------------------------------------------
   The demo subject's current track (the role switcher can change it)
   ------------------------------------------------------------------------ */

let currentTrack: TrainingTrack = 'strength';

export function getDemoTrack(): TrainingTrack {
  return currentTrack;
}

export function setDemoTrack(track: TrainingTrack): void {
  currentTrack = track;
}

/* ---------------------------------------------------------------------------
   People
   ------------------------------------------------------------------------ */

export const DEMO_LIFTER = { id: 'u-lifter-1', displayName: 'Alex Mercer', email: 'alex@lift2fit.com' };
export const DEMO_TRAINER = { id: 'u-trainer-1', displayName: 'Priya Raman', email: 'priya@lift2fit.com' };
export const DEMO_COACH = { id: 'u-coach-1', displayName: 'Jordan Blake', email: 'jordan@lift2fit.com' };

export const DEMO_INJURY_FLAGS = ['left_knee', 'lower_back'];

function baseUser(seed: { id: string; displayName: string; email: string }, roles: User['roles']): User {
  const meta = trackMeta(currentTrack);
  const isCoach = roles.includes('coach');
  return {
    id: seed.id,
    email: seed.email,
    displayName: seed.displayName,
    roles,
    unitSystem: 'metric',
    // The offline demo is not a support agent — 0038's flag is server-derived.
    supportStaff: false,
    tier: roles.includes('coach') ? 'premium' : 'basic',
    timezone: 'Europe/London',
    bodyweightKg: 82.4,
    heightCm: 178,
    trainingGoal: meta.label,
    experience: 'intermediate',
    injuryFlags: roles.includes('lifter') ? ['lower_back'] : [],
    weeklyTarget: 4,
    track: currentTrack,
    trackLabel: meta.label,
    // The demo coach stands in for the paid plan, so the lifter interface is
    // included; a non-coach always has it.
    coachPlan: isCoach ? 'coach' : 'free',
    lifterInterface: true,
    internalCoachOf: null,
  };
}

export function demoMe(role: User['roles'][number] = 'lifter'): User {
  if (role === 'coach') return baseUser(DEMO_COACH, ['lifter', 'trainer', 'coach']);
  if (role === 'trainer') return baseUser(DEMO_TRAINER, ['lifter', 'trainer']);
  return baseUser(DEMO_LIFTER, ['lifter']);
}

export function demoAuthResponse(
  email: string,
  displayName: string | null,
  accountType: 'lifter' | 'coach' = 'lifter',
): AuthResponse {
  const seed = displayName
    ? { id: 'u-demo-new', displayName, email }
    : { id: DEMO_LIFTER.id, displayName: DEMO_LIFTER.displayName, email: email || DEMO_LIFTER.email };
  // A business signup without a backend still reviews the workspace it is about
  // to own: the coach role, exactly as `POST /auth/register { accountType }`.
  const user = baseUser(seed, accountType === 'coach' ? ['lifter', 'coach'] : ['lifter']);
  return { accessToken: 'demo-access-token', refreshToken: 'demo-refresh-token', user };
}

/* ---------------------------------------------------------------------------
   Dates
   ------------------------------------------------------------------------ */

function mondayOf(date: Date): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d;
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** ISO dates for the last `count` weeks, oldest first, ending with this week. */
function weekStarts(count: number): string[] {
  const thisMonday = mondayOf(new Date());
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(thisMonday);
    d.setUTCDate(d.getUTCDate() - i * 7);
    out.push(isoDate(d));
  }
  return out;
}

function shiftIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return isoDate(d);
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

/* ---------------------------------------------------------------------------
   Per-track demo numbers
   ------------------------------------------------------------------------ */

interface TrackDemo {
  streak: number;
  longest: number;
  target: number;
  completed: number;
  dayFocus: string[];
  nextLabel: string;
  nextFocus: string[];
  nextExercises: number;
  nextSets: number;
  volumeKg7: number;
  volumeChangePct: number | null;
  sets7: number | null;
  prs7: number | null;
  avgRpe7: number | null;
  compliancePct7: number | null;
  metrics: Omit<TrackMetrics, 'coachPersona'>;
  trendSeries: 'volumeKg' | 'intervalMinutes' | 'mobilitySessions';
  trend: number[];
  weekStates: Week['days'][number]['state'][];
  feel: string;
}

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TRACK_DEMO: Record<TrainingTrack, TrackDemo> = {
  strength: {
    streak: 12,
    longest: 14,
    target: 4,
    completed: 3,
    dayFocus: ['LOWER', 'UPPER'],
    nextLabel: 'Day 4 · Upper volume',
    nextFocus: ['UPPER', 'HYPERTROPHY'],
    nextExercises: 5,
    nextSets: 18,
    volumeKg7: 18420,
    volumeChangePct: 6.4,
    sets7: 62,
    prs7: 3,
    avgRpe7: 7.6,
    compliancePct7: 92,
    metrics: {
      bestE1rmKg: 142.5,
      topSetLabel: '140 kg × 3',
      volumeKg7: 18420,
      volumeChangePct: 6.4,
      sets7: 62,
      prs7: 3,
      setsPerMuscle: 14,
      bodyweightKg: 82.4,
      intervalMinutes: null,
      workRestRatio: null,
      ergMetres: null,
      avgRpe: 7.6,
      powerMarkers: null,
      jumpHeightCm: null,
      readinessPct: null,
      mobilitySessions: null,
      painScore: null,
      consistencyPct: 92,
      sessions7: 3,
      feel: 'Strong, lower back quiet',
    },
    trendSeries: 'volumeKg',
    trend: [14200, 15100, 14900, 16400, 16100, 17250, 17300, 18420],
    weekStates: ['done', 'rest', 'done', 'today', 'rest', 'upcoming', 'rest'],
    feel: 'Strong, lower back quiet',
  },
  hypertrophy: {
    streak: 7,
    longest: 9,
    target: 5,
    completed: 4,
    dayFocus: ['PUSH', 'PULL'],
    nextLabel: 'Day 5 · Arms & shoulders',
    nextFocus: ['UPPER', 'ARMS'],
    nextExercises: 6,
    nextSets: 22,
    volumeKg7: 12680,
    volumeChangePct: 3.1,
    sets7: 74,
    prs7: 1,
    avgRpe7: 8.1,
    compliancePct7: 88,
    metrics: {
      bestE1rmKg: 96.4,
      topSetLabel: '32.5 kg × 12',
      volumeKg7: 12680,
      volumeChangePct: 3.1,
      sets7: 74,
      prs7: 1,
      setsPerMuscle: 17,
      bodyweightKg: 61.2,
      intervalMinutes: null,
      workRestRatio: null,
      ergMetres: null,
      avgRpe: 8.1,
      powerMarkers: null,
      jumpHeightCm: null,
      readinessPct: null,
      mobilitySessions: null,
      painScore: null,
      consistencyPct: 88,
      sessions7: 4,
      feel: 'Pumped, appetite up',
    },
    trendSeries: 'volumeKg',
    trend: [9800, 10400, 11200, 10900, 11800, 12100, 12300, 12680],
    weekStates: ['done', 'done', 'rest', 'done', 'today', 'upcoming', 'rest'],
    feel: 'Pumped, appetite up',
  },
  conditioning: {
    streak: 5,
    longest: 8,
    target: 3,
    completed: 2,
    dayFocus: ['ENGINE', 'CIRCUIT'],
    nextLabel: 'Day 3 · Capacity',
    nextFocus: ['ENGINE', 'CORE'],
    nextExercises: 5,
    nextSets: 16,
    volumeKg7: 0,
    volumeChangePct: null,
    sets7: 41,
    prs7: null,
    avgRpe7: 7.8,
    compliancePct7: 84,
    metrics: {
      bestE1rmKg: null,
      topSetLabel: null,
      volumeKg7: null,
      volumeChangePct: null,
      sets7: 41,
      prs7: null,
      setsPerMuscle: null,
      bodyweightKg: 78.9,
      intervalMinutes: 96,
      workRestRatio: '1:1',
      ergMetres: 24800,
      avgRpe: 7.8,
      powerMarkers: null,
      jumpHeightCm: null,
      readinessPct: null,
      mobilitySessions: null,
      painScore: null,
      consistencyPct: 84,
      sessions7: 3,
      feel: 'Engine good, legs heavy',
    },
    trendSeries: 'intervalMinutes',
    trend: [54, 62, 58, 71, 76, 69, 84, 96],
    weekStates: ['done', 'rest', 'done', 'today', 'rest', 'upcoming', 'rest'],
    feel: 'Engine good, legs heavy',
  },
  performance: {
    streak: 9,
    longest: 11,
    target: 4,
    completed: 3,
    dayFocus: ['POWER', 'SPEED-STRENGTH'],
    nextLabel: 'Day 4 · Power endurance',
    nextFocus: ['POWER', 'CONDITIONING'],
    nextExercises: 5,
    nextSets: 17,
    volumeKg7: 9240,
    volumeChangePct: 4.8,
    sets7: 46,
    prs7: 2,
    avgRpe7: 7.2,
    compliancePct7: 90,
    metrics: {
      bestE1rmKg: 168.0,
      topSetLabel: 'Box jump 58 cm',
      volumeKg7: 9240,
      volumeChangePct: 4.8,
      sets7: 46,
      prs7: 2,
      setsPerMuscle: null,
      bodyweightKg: 88.1,
      intervalMinutes: null,
      workRestRatio: null,
      ergMetres: null,
      avgRpe: 7.2,
      powerMarkers: 6,
      jumpHeightCm: 58,
      readinessPct: 84,
      mobilitySessions: null,
      painScore: null,
      consistencyPct: 90,
      sessions7: 3,
      feel: 'Springy, hips a little tight',
    },
    trendSeries: 'volumeKg',
    trend: [7100, 7600, 8200, 7900, 8600, 8900, 9100, 9240],
    weekStates: ['done', 'rest', 'done', 'today', 'done', 'upcoming', 'rest'],
    feel: 'Springy, hips a little tight',
  },
  wellness: {
    streak: 16,
    longest: 16,
    target: 5,
    completed: 4,
    dayFocus: ['MOBILITY', 'STABILITY'],
    nextLabel: 'Day 5 · Recovery flow',
    nextFocus: ['MOBILITY', 'BREATH'],
    nextExercises: 6,
    nextSets: 14,
    volumeKg7: 0,
    volumeChangePct: null,
    sets7: 38,
    prs7: null,
    avgRpe7: 3.4,
    compliancePct7: 94,
    metrics: {
      bestE1rmKg: null,
      topSetLabel: null,
      volumeKg7: null,
      volumeChangePct: null,
      sets7: 38,
      prs7: null,
      setsPerMuscle: null,
      bodyweightKg: 70.5,
      intervalMinutes: null,
      workRestRatio: null,
      ergMetres: null,
      avgRpe: 3.4,
      powerMarkers: null,
      jumpHeightCm: null,
      readinessPct: 78,
      mobilitySessions: 5,
      painScore: 3.2,
      consistencyPct: 94,
      sessions7: 5,
      feel: 'Loose, sleeping better',
    },
    trendSeries: 'mobilitySessions',
    trend: [3, 3, 4, 4, 4, 5, 5, 5],
    weekStates: ['done', 'done', 'today', 'done', 'rest', 'upcoming', 'rest'],
    feel: 'Loose, sleeping better',
  },
  general: {
    streak: 6,
    longest: 10,
    target: 3,
    completed: 2,
    dayFocus: ['FULL BODY', 'CORE'],
    nextLabel: 'Day 3 · Conditioning & core',
    nextFocus: ['ENGINE', 'CORE'],
    nextExercises: 5,
    nextSets: 15,
    volumeKg7: 7420,
    volumeChangePct: 2.2,
    sets7: 44,
    prs7: 2,
    avgRpe7: 6.9,
    compliancePct7: 80,
    metrics: {
      bestE1rmKg: 84.6,
      topSetLabel: '80 kg × 5',
      volumeKg7: 7420,
      volumeChangePct: 2.2,
      sets7: 44,
      prs7: 2,
      setsPerMuscle: 9,
      bodyweightKg: 75.0,
      intervalMinutes: 24,
      workRestRatio: '1:2',
      ergMetres: 4200,
      avgRpe: 6.9,
      powerMarkers: null,
      jumpHeightCm: null,
      readinessPct: 82,
      mobilitySessions: 2,
      painScore: null,
      consistencyPct: 80,
      sessions7: 3,
      feel: 'Steady, no niggles',
    },
    trendSeries: 'volumeKg',
    trend: [5800, 6100, 6400, 6200, 6800, 7100, 7250, 7420],
    weekStates: ['done', 'rest', 'done', 'today', 'rest', 'upcoming', 'rest'],
    feel: 'Steady, no niggles',
  },
};

function buildWeek(demo: TrackDemo): Week {
  const starts = weekStarts(1);
  const weekStart = starts[0]!;
  return {
    weekStart,
    target: demo.target,
    completed: demo.completed,
    days: demo.weekStates.map((state, index) => ({
      date: shiftIso(weekStart, index),
      state,
      label: state === 'done' ? demo.dayFocus[index % demo.dayFocus.length]! : state === 'today' ? 'TODAY' : null,
      sessionId: state === 'done' ? `s-${weekStart}-${index}` : state === 'today' ? `s-${weekStart}-today` : null,
    })),
  };
}

function buildTrend(demo: TrackDemo): TrendPoint[] {
  const starts = weekStarts(8);
  return starts.map((weekStart, index) => {
    const value = demo.trend[index] ?? 0;
    const point: TrendPoint = { weekStart };
    if (demo.trendSeries === 'volumeKg') point.volumeKg = value;
    if (demo.trendSeries === 'intervalMinutes') point.intervalMinutes = value;
    if (demo.trendSeries === 'mobilitySessions') point.mobilitySessions = value;
    point.sets = Math.round(value / 300) || Math.round(value * 7);
    return point;
  });
}

/* ---------------------------------------------------------------------------
   Recent highlights (track-appropriate)
   ------------------------------------------------------------------------ */

const HIGHLIGHTS: Record<TrainingTrack, Highlight[]> = {
  strength: [],
  hypertrophy: [],
  conditioning: [
    { label: 'Rower · 6 × 500 m', value: '1:48 avg /500 m', detail: '2 min rest', at: daysAgoIso(1) },
    { label: 'Ski erg · 10 × 1 min', value: '14 200 m total', detail: '1 min rest', at: daysAgoIso(3) },
    { label: 'Sled push · 6 × 20 m', value: 'Heaviest 92.5 kg', detail: 'walk-back rest', at: daysAgoIso(5) },
    { label: 'Bike erg · 3 × 8 min', value: '248 W avg', detail: '2 min rest', at: daysAgoIso(8) },
    { label: 'Farmer carry · 4 × 40 m', value: '2 × 32.5 kg', detail: '1 min rest', at: daysAgoIso(10) },
  ],
  performance: [
    { label: 'Box jump', value: '58 cm', detail: '5 × 3, all clean', at: daysAgoIso(1) },
    { label: 'Med-ball throw', value: '9.4 m', detail: '4 kg, seated', at: daysAgoIso(3) },
    { label: 'Trap-bar jump', value: '62.5 kg × 3', detail: 'peak intent', at: daysAgoIso(5) },
    { label: 'Sled acceleration · 6 × 15 m', value: 'Best 3.05 s', detail: 'timed gate, indoors', at: daysAgoIso(8) },
    { label: 'Plyo push-up', value: '22 reps', detail: 'bodyweight AMRAP', at: daysAgoIso(10) },
  ],
  wellness: [
    { label: 'Mat mobility flow', value: '25 min', detail: 'hips + thoracic', at: daysAgoIso(0) },
    { label: 'Pain score', value: '3 / 10', detail: 'down from 5 last week', at: daysAgoIso(1) },
    { label: 'Stability circuit', value: '3 rounds', detail: 'dead bug · bird dog · side plank', at: daysAgoIso(2) },
    { label: 'Sleep', value: '7 h 30', detail: 'self-reported', at: daysAgoIso(3) },
    { label: 'Banded shoulder series', value: '3 × 12', detail: 'no pain reported', at: daysAgoIso(5) },
  ],
  general: [],
};

const PERSONAL_RECORDS: PersonalRecordEntry[] = [
  { exerciseName: 'Back Squat', weightKg: 140, reps: 3, estimated1rmKg: 154, achievedAt: daysAgoIso(2) },
  { exerciseName: 'Bench Press', weightKg: 102.5, reps: 5, estimated1rmKg: 119.6, achievedAt: daysAgoIso(4) },
  { exerciseName: 'Deadlift', weightKg: 180, reps: 3, estimated1rmKg: 198, achievedAt: daysAgoIso(9) },
  { exerciseName: 'Overhead Press', weightKg: 62.5, reps: 6, estimated1rmKg: 75, achievedAt: daysAgoIso(12) },
  { exerciseName: 'Barbell Row', weightKg: 95, reps: 8, estimated1rmKg: 120.3, achievedAt: daysAgoIso(15) },
];

/* ---------------------------------------------------------------------------
   Lifter dashboard
   ------------------------------------------------------------------------ */

export function demoLifterDashboard(): LifterDashboard {
  const track = currentTrack;
  const meta = trackMeta(track);
  const demo = TRACK_DEMO[track];
  const streak: StreakSummary = {
    currentWeeks: demo.streak,
    longestWeeks: demo.longest,
    qualifiesThisWeek: demo.completed >= demo.target - 1,
  };

  const metrics: TrackMetrics = { coachPersona: meta.persona, ...demo.metrics };

  const alerts: LifterDashboard['alerts'] =
    track === 'wellness'
      ? [
          { code: 'pain_tracking', severity: 'info', message: 'Pain score logged at 3/10 — keep the sessions low-impact.' },
          { code: 'consistency', severity: 'warning', message: 'One more mobility session this week keeps the streak.' },
        ]
      : track === 'conditioning'
        ? [
            { code: 'work_rest', severity: 'info', message: 'Work:rest drifting to 1:0.8 on the erg — hold 1:1.' },
            { code: 'legs_heavy', severity: 'warning', message: 'Sled volume up 20% this week — keep one easy day.' },
          ]
        : [
            { code: 'rpe_creep', severity: 'warning', message: 'Average RPE up 0.6 on the squat — hold 8 for one more week.' },
            { code: 'deload', severity: 'info', message: 'Deload week lands in 2 weeks. Keep the top sets clean.' },
          ];

  const coachVerdict =
    track === 'wellness' ? 'Gentle progress' : track === 'conditioning' ? 'Engine building' : 'On track';

  const coachHeadline =
    track === 'wellness'
      ? 'Pain trending down and consistency at 94%. Keep the mobility work daily and hold the low-impact strength.'
      : track === 'conditioning'
        ? 'Interval minutes up 14% with work:rest held at 1:1. The erg splits are holding under fatigue.'
        : track === 'hypertrophy'
          ? 'Weekly volume up 3.1% with 17 sets per muscle in range. Bodyweight gaining at the right rate.'
          : track === 'performance'
            ? 'Jump height up 2 cm and readiness at 84%. Power markers are moving before load is added.'
            : 'Volume up 6.4% and the squat e1RM is at 142.5 kg. Two more weeks at this load, then a deload.';

  return {
    role: 'lifter',
    track,
    trackLabel: meta.label,
    metricsFocus: [...meta.metricsFocus],
    trackMetrics: metrics,
    greeting: { displayName: DEMO_LIFTER.displayName, localDate: isoDate(new Date()) },
    streak,
    week: buildWeek(demo),
    nextSession: {
      sessionId: `s-${track}-next`,
      dayLabel: demo.nextLabel,
      focus: demo.nextFocus,
      exercises: demo.nextExercises,
      sets: demo.nextSets,
      lastPerformed: daysAgoIso(track === 'wellness' ? 3 : 7),
    },
    totals: {
      volumeKg7: demo.volumeKg7,
      volumeChangePct: demo.volumeChangePct,
      sets7: demo.sets7 ?? 0,
      prs7: demo.prs7 ?? 0,
      avgRpe7: demo.avgRpe7,
      compliancePct7: demo.compliancePct7,
    },
    trend: buildTrend(demo),
    personalRecords: track === 'wellness' || track === 'conditioning' ? [] : PERSONAL_RECORDS,
    highlights: HIGHLIGHTS[track],
    coach: {
      hasPremium: track !== 'general',
      latestReportAt: daysAgoIso(1),
      verdict: coachVerdict,
      headline: coachHeadline,
      changes: track === 'wellness' ? 3 : 5,
      persona: meta.persona,
    },
    alerts,
  };
}

/* ---------------------------------------------------------------------------
   Roster — trainees across tracks (indoor gym work only)
   ------------------------------------------------------------------------ */

interface TraineeSeed {
  userId: string;
  displayName: string;
  email: string;
  track: TrainingTrack;
  trainerName: string;
  status: Trainee['status'];
  lastSessionDays: number | null;
  sessionsLast7: number;
  compliancePct: number | null;
  streak: number;
  flags: string[];
  focus: string;
}

const TRAINEE_SEEDS: TraineeSeed[] = [
  {
    userId: 'u-t-1',
    displayName: 'Marcus Bell',
    email: 'marcus@example.com',
    track: 'conditioning',
    trainerName: DEMO_TRAINER.displayName,
    status: 'active',
    lastSessionDays: 13,
    sessionsLast7: 0,
    compliancePct: 41,
    streak: 0,
    flags: ['missed_2_weeks', 'rpe_creep'],
    focus: 'Rower + sled intervals',
  },
  {
    userId: 'u-t-2',
    displayName: 'Dana Okafor',
    email: 'dana@example.com',
    track: 'performance',
    trainerName: DEMO_TRAINER.displayName,
    status: 'active',
    lastSessionDays: 2,
    sessionsLast7: 3,
    compliancePct: 78,
    streak: 9,
    flags: ['pain_reported'],
    focus: 'Plyos + trap-bar power',
  },
  {
    userId: 'u-t-3',
    displayName: 'Tomás Ferreira',
    email: 'tomas@example.com',
    track: 'hypertrophy',
    trainerName: DEMO_TRAINER.displayName,
    status: 'active',
    lastSessionDays: 1,
    sessionsLast7: 5,
    compliancePct: 96,
    streak: 14,
    flags: [],
    focus: 'Push / pull / legs',
  },
  {
    userId: 'u-t-4',
    displayName: 'Ruth Alvarez',
    email: 'ruth@example.com',
    track: 'wellness',
    trainerName: DEMO_TRAINER.displayName,
    status: 'active',
    lastSessionDays: 0,
    sessionsLast7: 5,
    compliancePct: 94,
    streak: 16,
    flags: [],
    focus: 'Mobility + stability',
  },
  {
    userId: 'u-t-5',
    displayName: 'Grace Lin',
    email: 'grace@example.com',
    track: 'strength',
    trainerName: DEMO_TRAINER.displayName,
    status: 'invited',
    lastSessionDays: null,
    sessionsLast7: 0,
    compliancePct: null,
    streak: 0,
    flags: ['invite_pending'],
    focus: 'Awaiting first session',
  },
  {
    userId: 'u-t-6',
    displayName: 'Owen Pryce',
    email: 'owen@example.com',
    track: 'strength',
    trainerName: DEMO_COACH.displayName,
    status: 'active',
    lastSessionDays: 4,
    sessionsLast7: 2,
    compliancePct: 86,
    streak: 6,
    flags: [],
    focus: 'Squat / bench / deadlift',
  },
  {
    userId: 'u-t-7',
    displayName: 'Hana Sato',
    email: 'hana@example.com',
    track: 'conditioning',
    trainerName: DEMO_COACH.displayName,
    status: 'active',
    lastSessionDays: 3,
    sessionsLast7: 4,
    compliancePct: 91,
    streak: 11,
    flags: [],
    focus: 'Ski erg + carries',
  },
  {
    userId: 'u-t-8',
    displayName: 'Elias Novak',
    email: 'elias@example.com',
    track: 'wellness',
    trainerName: DEMO_COACH.displayName,
    status: 'invited',
    lastSessionDays: null,
    sessionsLast7: 0,
    compliancePct: null,
    streak: 0,
    flags: ['invite_pending'],
    focus: 'Awaiting first check-in',
  },
];

function toTrainee(seed: TraineeSeed): Trainee {
  const meta = trackMeta(seed.track);
  return {
    userId: seed.userId,
    displayName: seed.displayName,
    email: seed.email,
    status: seed.status,
    linkedAt: seed.status === 'invited' ? null : daysAgoIso(40 + seed.sessionsLast7),
    lastSessionAt: seed.lastSessionDays === null ? null : daysAgoIso(seed.lastSessionDays),
    sessionsLast7: seed.sessionsLast7,
    compliancePct: seed.compliancePct,
    currentStreakWeeks: seed.streak,
    flags: seed.flags,
    sharedFields: ['sessions', 'sets', 'notes'],
    track: seed.track,
    trackLabel: meta.label,
    coachPersona: meta.persona,
    trainerName: seed.trainerName,
    focus: seed.focus,
  };
}

export function demoTrainees(trainerName?: string): Trainee[] {
  const all = TRAINEE_SEEDS.map(toTrainee);
  const scoped = trainerName ? all.filter((t) => t.trainerName === trainerName) : all;
  return scoped.sort((a, b) => {
    if (a.flags.length !== b.flags.length) return b.flags.length - a.flags.length;
    const aTime = a.lastSessionAt ? Date.parse(a.lastSessionAt) : 0;
    const bTime = b.lastSessionAt ? Date.parse(b.lastSessionAt) : 0;
    return aTime - bTime;
  });
}

export function demoInviteTrainee(email: string): Trainee {
  return {
    userId: `u-invite-${email.replace(/[^a-z0-9]/gi, '').slice(0, 8) || 'new'}`,
    displayName: email.split('@')[0] ?? 'New client',
    email,
    status: 'invited',
    linkedAt: null,
    lastSessionAt: null,
    sessionsLast7: 0,
    compliancePct: null,
    currentStreakWeeks: 0,
    flags: ['invite_pending'],
    sharedFields: ['sessions', 'sets'],
    track: currentTrack,
    trackLabel: trackMeta(currentTrack).label,
    coachPersona: trackMeta(currentTrack).persona,
    trainerName: DEMO_TRAINER.displayName,
    focus: 'Invite sent — awaiting their first session',
  };
}

/** Injury flags offered in the profile and the plan wizard. */
export const INJURY_FLAGS_ALL = ['lower_back', 'shoulder', 'knee', 'hip', 'elbow', 'wrist', 'ankle', 'neck'];

const ATTENTION_SEEDS: RosterDashboard['attention'] = [
  {
    userId: 'u-t-1',
    displayName: 'Marcus Bell',
    reason: 'No session logged for 13 days — start with a short erg interval block.',
    severity: 'danger',
  },
  {
    userId: 'u-t-2',
    displayName: 'Dana Okafor',
    reason: 'Pain reported on plyometrics — swap to sled accelerations this week.',
    severity: 'warning',
  },
  {
    userId: 'u-t-5',
    displayName: 'Grace Lin',
    reason: 'Invite still pending — resend the link.',
    severity: 'info',
  },
];

export function demoRosterDashboard(role: 'trainer' | 'coach'): RosterDashboard {
  const meta = trackMeta(currentTrack);
  const demo = TRACK_DEMO[currentTrack];
  const mine = role === 'trainer' ? demoTrainees(DEMO_TRAINER.displayName) : demoTrainees();
  const active = mine.filter((t) => t.status === 'active');
  const invited = mine.filter((t) => t.status === 'invited');
  const flagged = mine.filter((t) => t.flags.length > 0);
  const withCompliance = active.filter((t) => t.compliancePct !== null);
  const avgCompliance = withCompliance.length
    ? Math.round(withCompliance.reduce((sum, t) => sum + (t.compliancePct ?? 0), 0) / withCompliance.length)
    : null;

  const seed = {
    id: role === 'coach' ? DEMO_COACH.id : DEMO_TRAINER.id,
    displayName: role === 'coach' ? DEMO_COACH.displayName : DEMO_TRAINER.displayName,
    email: role === 'coach' ? DEMO_COACH.email : DEMO_TRAINER.email,
  };

  return {
    role,
    track: currentTrack,
    trackLabel: meta.label,
    metricsFocus: [...meta.metricsFocus],
    trackMetrics: { coachPersona: meta.persona, ...demo.metrics },
    greeting: { displayName: seed.displayName, localDate: isoDate(new Date()) },
    counts: { trainees: mine.length, active: active.length, invited: invited.length, flagged: flagged.length },
    averages: {
      compliancePct: avgCompliance,
      sessionsPerTrainee7: active.length
        ? Math.round((active.reduce((sum, t) => sum + t.sessionsLast7, 0) / active.length) * 10) / 10
        : 0,
      volumeKg7: role === 'coach' ? 48210 : 21340,
    },
    trainees: mine,
    attention: ATTENTION_SEEDS.filter((a) => mine.some((t) => t.userId === a.userId)),
    ownWeek: buildWeek(demo),
    ownStreak: { currentWeeks: demo.streak, longestWeeks: demo.longest, qualifiesThisWeek: demo.completed >= demo.target - 1 },
  };
}

export function demoDashboard(role: 'lifter' | 'trainer' | 'coach'): DashboardPayload {
  return role === 'lifter' ? demoLifterDashboard() : demoRosterDashboard(role);
}

/* ---------------------------------------------------------------------------
   Programme
   ------------------------------------------------------------------------ */

let planCounter = 0;

function ex(
  movement: string,
  variant: string | null,
  equipment: string,
  sets: number,
  repMin: number,
  repMax: number,
  overrides: Partial<PlanExercise> = {},
): PlanExercise {
  return {
    movement,
    variant,
    equipment,
    sets,
    repMin,
    repMax,
    perSide: false,
    measure: 'reps',
    targetRpe: 7,
    targetWeightKg: null,
    percent1rm: null,
    tempo: null,
    restSeconds: 90,
    supersetGroup: null,
    notes: null,
    ...overrides,
  };
}

const TRACK_DAYS: Record<TrainingTrack, Array<{ label: string; focus: string[]; exercises: PlanExercise[] }>> = {
  strength: [
    {
      label: 'Day 1 · Lower',
      focus: ['LOWER', 'SQUAT'],
      exercises: [
        ex('Back Squat', 'High bar', 'barbell', 4, 4, 4, { targetRpe: 8, percent1rm: 82.5, restSeconds: 180, notes: 'Add 2.5 kg when all reps are clean.' }),
        ex('Romanian Deadlift', null, 'barbell', 3, 6, 6, { targetRpe: 7.5, restSeconds: 150 }),
        ex('Leg Press', null, 'machine', 3, 10, 10, { targetRpe: 8, restSeconds: 120 }),
        ex('Nordic Curl', 'Assisted', 'bodyweight', 3, 6, 6, { targetRpe: 8, tempo: '3-1-1-0', restSeconds: 90 }),
        ex('Standing Calf Raise', null, 'machine', 4, 8, 8, { targetRpe: 8, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 2 · Upper',
      focus: ['UPPER', 'PRESS'],
      exercises: [
        ex('Bench Press', 'Competition', 'barbell', 4, 5, 5, { targetRpe: 8, percent1rm: 80, restSeconds: 180 }),
        ex('Barbell Row', 'Pendlay', 'barbell', 4, 6, 6, { targetRpe: 7.5, restSeconds: 150 }),
        ex('Overhead Press', 'Standing', 'barbell', 3, 8, 8, { targetRpe: 8, restSeconds: 120 }),
        ex('Lat Pulldown', 'Neutral grip', 'cable', 3, 10, 10, { targetRpe: 8, restSeconds: 90 }),
        ex('Cable Triceps Pressdown', 'Rope', 'cable', 3, 12, 12, { targetRpe: 8, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 3 · Lower posterior',
      focus: ['LOWER', 'HINGE'],
      exercises: [
        ex('Deadlift', 'Conventional', 'barbell', 4, 3, 3, { targetRpe: 8, percent1rm: 85, restSeconds: 210 }),
        ex('Front Squat', null, 'barbell', 3, 5, 5, { targetRpe: 7.5, restSeconds: 180 }),
        ex('Hip Thrust', 'Barbell', 'barbell', 3, 8, 8, { targetRpe: 8, restSeconds: 120 }),
        ex('Seated Leg Curl', null, 'machine', 3, 12, 12, { targetRpe: 8, restSeconds: 75 }),
        ex('Hanging Knee Raise', null, 'bodyweight', 3, 12, 12, { targetRpe: 8, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 4 · Upper volume',
      focus: ['UPPER', 'HYPERTROPHY'],
      exercises: [
        ex('Incline Dumbbell Press', '30°', 'dumbbell', 4, 8, 8, { targetRpe: 8, restSeconds: 120 }),
        ex('Weighted Pull-up', null, 'bodyweight', 4, 6, 6, { targetRpe: 8, supersetGroup: 1, restSeconds: 90 }),
        ex('Dumbbell Lateral Raise', null, 'dumbbell', 3, 15, 15, { targetRpe: 9, supersetGroup: 1, restSeconds: 60 }),
        ex('Cable Row', 'Seated', 'cable', 3, 12, 12, { targetRpe: 8, restSeconds: 90 }),
        ex('Barbell Curl', null, 'barbell', 3, 10, 10, { targetRpe: 8, restSeconds: 60 }),
      ],
    },
  ],
  hypertrophy: [
    {
      label: 'Day 1 · Push',
      focus: ['CHEST', 'SHOULDERS'],
      exercises: [
        ex('Incline Dumbbell Press', '30°', 'dumbbell', 4, 8, 10, { targetRpe: 8, restSeconds: 120 }),
        ex('Machine Chest Press', null, 'machine', 3, 12, 12, { targetRpe: 8, restSeconds: 90 }),
        ex('Cable Lateral Raise', null, 'cable', 4, 15, 15, { targetRpe: 9, restSeconds: 60 }),
        ex('Cable Triceps Pressdown', 'Bar', 'cable', 3, 12, 15, { targetRpe: 8, supersetGroup: 2, restSeconds: 60 }),
        ex('Overhead Cable Extension', 'Rope', 'cable', 3, 12, 12, { targetRpe: 8, supersetGroup: 2, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 2 · Pull',
      focus: ['BACK', 'BICEPS'],
      exercises: [
        ex('Lat Pulldown', 'Wide grip', 'cable', 4, 10, 10, { targetRpe: 8, restSeconds: 90 }),
        ex('Chest-Supported Row', null, 'machine', 4, 10, 10, { targetRpe: 8, restSeconds: 90 }),
        ex('Cable Face Pull', null, 'cable', 3, 15, 15, { targetRpe: 8, restSeconds: 60 }),
        ex('Machine Preacher Curl', null, 'machine', 3, 12, 12, { targetRpe: 9, restSeconds: 60 }),
        ex('Dumbbell Shrug', null, 'dumbbell', 3, 12, 12, { targetRpe: 8, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 3 · Legs',
      focus: ['QUADS', 'GLUTES'],
      exercises: [
        ex('Leg Press', null, 'machine', 4, 10, 12, { targetRpe: 8, restSeconds: 120 }),
        ex('Bulgarian Split Squat', null, 'dumbbell', 3, 10, 10, { perSide: true, targetRpe: 8, restSeconds: 90 }),
        ex('Leg Extension', null, 'machine', 4, 12, 12, { targetRpe: 9, restSeconds: 75 }),
        ex('Seated Leg Curl', null, 'machine', 4, 12, 12, { targetRpe: 8, restSeconds: 75 }),
        ex('Standing Calf Raise', null, 'machine', 4, 15, 15, { targetRpe: 9, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 4 · Upper & arms',
      focus: ['UPPER', 'ARMS'],
      exercises: [
        ex('Dumbbell Bench Press', null, 'dumbbell', 4, 10, 10, { targetRpe: 8, restSeconds: 105 }),
        ex('Cable Row', 'Seated', 'cable', 4, 12, 12, { targetRpe: 8, restSeconds: 90 }),
        ex('Dumbbell Lateral Raise', null, 'dumbbell', 4, 15, 15, { targetRpe: 9, supersetGroup: 1, restSeconds: 60 }),
        ex('Cable Curl', 'Straight bar', 'cable', 3, 12, 12, { targetRpe: 9, supersetGroup: 1, restSeconds: 60 }),
        ex('Machine Dip', 'Assisted', 'machine', 3, 10, 12, { targetRpe: 8, restSeconds: 75 }),
      ],
    },
  ],
  conditioning: [
    {
      label: 'Day 1 · Engine',
      focus: ['ENGINE', 'INTERVALS'],
      exercises: [
        ex('Rower', 'Concept2', 'machine', 6, 500, 500, { measure: 'distance_m', targetRpe: 8, restSeconds: 120, notes: 'Hold each 500 m at 1:50 or better.' }),
        ex('Ski Erg', null, 'machine', 8, 60, 60, { measure: 'time_s', targetRpe: 8, restSeconds: 60, notes: '1 min hard, 1 min easy.' }),
        ex('Bike Erg', null, 'machine', 3, 480, 480, { measure: 'time_s', targetRpe: 7, restSeconds: 120, notes: '8 min at threshold.' }),
        ex('Sled Push', 'Heavy', 'machine', 6, 20, 20, { measure: 'distance_m', targetRpe: 8, restSeconds: 90, notes: 'Return slowly for the rest.' }),
      ],
    },
    {
      label: 'Day 2 · Circuit',
      focus: ['CIRCUIT', 'FULL BODY'],
      exercises: [
        ex('Kettlebell Swing', 'Russian', 'kettlebell', 5, 15, 15, { targetRpe: 7, supersetGroup: 1, restSeconds: 45 }),
        ex('Med Ball Slam', null, 'bodyweight', 5, 12, 12, { targetRpe: 7, supersetGroup: 1, restSeconds: 45 }),
        ex('Sled Drag', 'Backwards', 'machine', 5, 25, 25, { measure: 'distance_m', targetRpe: 8, supersetGroup: 1, restSeconds: 60 }),
        ex('Farmer Carry', null, 'dumbbell', 4, 40, 40, { measure: 'distance_m', targetRpe: 8, restSeconds: 75 }),
        ex('Assault Bike', 'Sprint', 'machine', 6, 45, 45, { measure: 'time_s', targetRpe: 9, restSeconds: 90 }),
      ],
    },
    {
      label: 'Day 3 · Capacity',
      focus: ['ENGINE', 'CORE'],
      exercises: [
        ex('Rower', 'Concept2', 'machine', 5, 1000, 1000, { measure: 'distance_m', targetRpe: 7, restSeconds: 150, notes: '1 000 m repeats.' }),
        ex('Bike Erg', null, 'machine', 3, 480, 480, { measure: 'time_s', targetRpe: 7, restSeconds: 120 }),
        ex('Sled Push', 'Moderate', 'machine', 8, 15, 15, { measure: 'distance_m', targetRpe: 8, restSeconds: 60 }),
        ex('Plank', null, 'bodyweight', 3, 60, 60, { measure: 'time_s', targetRpe: 7, restSeconds: 45 }),
      ],
    },
    {
      label: 'Day 4 · Mixed ergs',
      focus: ['INTERVALS', 'GRIP'],
      exercises: [
        ex('Ski Erg', null, 'machine', 10, 250, 250, { measure: 'distance_m', targetRpe: 8, restSeconds: 60, notes: '250 m repeats.' }),
        ex('Kettlebell Clean', 'Single arm', 'kettlebell', 4, 10, 10, { perSide: true, targetRpe: 7, restSeconds: 75 }),
        ex('Box Step-up', 'Weighted', 'dumbbell', 4, 12, 12, { perSide: true, targetRpe: 7, restSeconds: 75 }),
        ex('Farmer Carry', null, 'dumbbell', 5, 30, 30, { measure: 'distance_m', targetRpe: 8, restSeconds: 60 }),
      ],
    },
  ],
  performance: [
    {
      label: 'Day 1 · Lower power',
      focus: ['POWER', 'JUMPS'],
      exercises: [
        ex('Box Jump', 'Low box', 'bodyweight', 5, 3, 3, { targetRpe: 6, restSeconds: 120, notes: 'Step down, reset each rep.' }),
        ex('Broad Jump', null, 'bodyweight', 5, 3, 3, { targetRpe: 7, restSeconds: 120, notes: 'Mark the best landing distance and beat it next week.' }),
        ex('Trap Bar Jump', null, 'barbell', 4, 3, 3, { targetRpe: 7, restSeconds: 150 }),
        ex('Back Squat', 'Speed', 'barbell', 4, 3, 3, { targetRpe: 7, percent1rm: 70, restSeconds: 150 }),
        ex('Sled Acceleration', null, 'machine', 6, 15, 15, { measure: 'distance_m', targetRpe: 8, restSeconds: 120 }),
      ],
    },
    {
      label: 'Day 2 · Upper power',
      focus: ['POWER', 'THROWS'],
      exercises: [
        ex('Plyo Push-up', null, 'bodyweight', 4, 5, 5, { targetRpe: 6, restSeconds: 90 }),
        ex('Med Ball Chest Throw', '4 kg', 'bodyweight', 4, 4, 4, { targetRpe: 7, restSeconds: 90 }),
        ex('Bench Press', 'Speed', 'barbell', 4, 3, 3, { targetRpe: 7, percent1rm: 65, restSeconds: 150 }),
        ex('Weighted Pull-up', null, 'bodyweight', 4, 5, 5, { targetRpe: 8, restSeconds: 120 }),
        ex('Cable Rotation', 'Half-kneeling', 'cable', 3, 10, 10, { perSide: true, targetRpe: 7, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 3 · Plyometrics',
      focus: ['PLYO', 'POWER'],
      exercises: [
        ex('Depth Drop', 'Low box', 'bodyweight', 4, 4, 4, { targetRpe: 6, restSeconds: 120, notes: 'Land quietly, stick it.' }),
        ex('Med Ball Overhead Throw', '5 kg', 'bodyweight', 5, 9, 9, { measure: 'distance_m', targetRpe: 7, restSeconds: 90 }),
        ex('Trap Bar Deadlift', 'Speed', 'barbell', 4, 3, 3, { targetRpe: 7, percent1rm: 72.5, restSeconds: 180 }),
        ex('Split Squat', null, 'dumbbell', 3, 6, 6, { perSide: true, targetRpe: 8, restSeconds: 90 }),
        ex('Sled Push', 'Heavy', 'machine', 6, 20, 20, { measure: 'distance_m', targetRpe: 8, restSeconds: 120 }),
      ],
    },
    {
      label: 'Day 4 · Power endurance',
      focus: ['POWER', 'CONDITIONING'],
      exercises: [
        ex('Med Ball Slam', null, 'bodyweight', 5, 8, 8, { targetRpe: 7, supersetGroup: 1, restSeconds: 45 }),
        ex('Kettlebell Swing', 'Hardstyle', 'kettlebell', 5, 10, 10, { targetRpe: 7, supersetGroup: 1, restSeconds: 45 }),
        ex('Box Jump', 'Moderate box', 'bodyweight', 5, 5, 5, { targetRpe: 7, restSeconds: 90 }),
        ex('Sled Drag', null, 'machine', 6, 25, 25, { measure: 'distance_m', targetRpe: 8, restSeconds: 75 }),
        ex('Plank', 'Weighted', 'bodyweight', 3, 45, 45, { measure: 'time_s', targetRpe: 7, restSeconds: 45 }),
      ],
    },
  ],
  wellness: [
    {
      label: 'Day 1 · Mobility flow',
      focus: ['MOBILITY', 'HIPS'],
      exercises: [
        ex('Cat-Cow', null, 'bodyweight', 2, 8, 8, { targetRpe: 3, tempo: '2-2-2-0', restSeconds: 30 }),
        ex('90/90 Hip Switch', null, 'bodyweight', 3, 8, 8, { perSide: true, targetRpe: 3, restSeconds: 30 }),
        ex('Thoracic Rotation', 'Open book', 'bodyweight', 3, 8, 8, { perSide: true, targetRpe: 3, restSeconds: 30 }),
        ex('Hamstring Stretch', 'Supine strap', 'band', 2, 45, 45, { measure: 'time_s', targetRpe: 3, restSeconds: 20 }),
        ex("Child's Pose", null, 'bodyweight', 2, 60, 60, { measure: 'time_s', targetRpe: 2, restSeconds: 20 }),
      ],
    },
    {
      label: 'Day 2 · Stability',
      focus: ['STABILITY', 'CORE'],
      exercises: [
        ex('Dead Bug', null, 'bodyweight', 3, 10, 10, { perSide: true, targetRpe: 4, restSeconds: 45 }),
        ex('Bird Dog', null, 'bodyweight', 3, 10, 10, { perSide: true, targetRpe: 4, restSeconds: 45 }),
        ex('Glute Bridge', null, 'bodyweight', 3, 12, 12, { targetRpe: 4, tempo: '2-1-1-0', restSeconds: 45 }),
        ex('Side Plank', null, 'bodyweight', 3, 30, 30, { measure: 'time_s', perSide: true, targetRpe: 4, restSeconds: 45 }),
        ex('Banded Clamshell', null, 'band', 3, 15, 15, { perSide: true, targetRpe: 4, restSeconds: 30 }),
      ],
    },
    {
      label: 'Day 3 · Low-impact strength',
      focus: ['LOW IMPACT', 'STRENGTH'],
      exercises: [
        ex('Bodyweight Squat', 'Box supported', 'bodyweight', 3, 12, 12, { targetRpe: 5, restSeconds: 60 }),
        ex('Incline Push-up', null, 'bodyweight', 3, 10, 10, { targetRpe: 5, restSeconds: 60 }),
        ex('Cable Row', 'Light', 'cable', 3, 12, 12, { targetRpe: 4, restSeconds: 60 }),
        ex('Seated Leg Curl', 'Light', 'machine', 3, 12, 12, { targetRpe: 4, restSeconds: 60 }),
        ex('Calf Raise', 'Supported', 'bodyweight', 3, 15, 15, { targetRpe: 4, restSeconds: 45 }),
      ],
    },
    {
      label: 'Day 4 · Recovery',
      focus: ['RECOVERY', 'BREATH'],
      exercises: [
        ex('Diaphragmatic Breathing', null, 'bodyweight', 2, 10, 10, { targetRpe: 1, restSeconds: 30 }),
        ex('Foam Roll Circuit', null, 'bodyweight', 3, 60, 60, { measure: 'time_s', targetRpe: 2, restSeconds: 30 }),
        ex('Hip Flexor Stretch', 'Half-kneeling', 'bodyweight', 2, 45, 45, { measure: 'time_s', perSide: true, targetRpe: 2, restSeconds: 20 }),
        ex('Shoulder Dislocate', null, 'band', 2, 12, 12, { targetRpe: 2, restSeconds: 30 }),
        ex('Mat Mobility', 'Full body', 'bodyweight', 1, 900, 900, { measure: 'time_s', targetRpe: 2, restSeconds: 0 }),
      ],
    },
  ],
  general: [
    {
      label: 'Day 1 · Full body A',
      focus: ['FULL BODY'],
      exercises: [
        ex('Goblet Squat', null, 'kettlebell', 3, 10, 10, { targetRpe: 6, restSeconds: 90 }),
        ex('Dumbbell Bench Press', null, 'dumbbell', 3, 10, 10, { targetRpe: 6, restSeconds: 90 }),
        ex('Lat Pulldown', null, 'cable', 3, 12, 12, { targetRpe: 6, restSeconds: 75 }),
        ex('Romanian Deadlift', null, 'dumbbell', 3, 10, 10, { targetRpe: 6, restSeconds: 90 }),
        ex('Plank', null, 'bodyweight', 3, 40, 40, { measure: 'time_s', targetRpe: 6, restSeconds: 45 }),
      ],
    },
    {
      label: 'Day 2 · Full body B',
      focus: ['FULL BODY', 'STRENGTH'],
      exercises: [
        ex('Leg Press', null, 'machine', 3, 12, 12, { targetRpe: 6, restSeconds: 90 }),
        ex('Push-up', null, 'bodyweight', 3, 12, 12, { targetRpe: 6, restSeconds: 60 }),
        ex('Cable Row', 'Seated', 'cable', 3, 12, 12, { targetRpe: 6, restSeconds: 75 }),
        ex('Dumbbell Shoulder Press', 'Seated', 'dumbbell', 3, 12, 12, { targetRpe: 6, restSeconds: 75 }),
        ex('Farmer Carry', null, 'dumbbell', 3, 40, 40, { measure: 'distance_m', targetRpe: 6, restSeconds: 60 }),
      ],
    },
    {
      label: 'Day 3 · Conditioning & core',
      focus: ['ENGINE', 'CORE'],
      exercises: [
        ex('Rower', 'Concept2', 'machine', 8, 250, 250, { measure: 'distance_m', targetRpe: 7, restSeconds: 75 }),
        ex('Kettlebell Swing', 'Russian', 'kettlebell', 4, 15, 15, { targetRpe: 7, supersetGroup: 1, restSeconds: 45 }),
        ex('Med Ball Slam', null, 'bodyweight', 4, 12, 12, { targetRpe: 7, supersetGroup: 1, restSeconds: 45 }),
        ex('Dead Bug', null, 'bodyweight', 3, 10, 10, { perSide: true, targetRpe: 5, restSeconds: 45 }),
        ex('Side Plank', null, 'bodyweight', 3, 30, 30, { measure: 'time_s', perSide: true, targetRpe: 5, restSeconds: 45 }),
      ],
    },
  ],
};

const TRACK_SUMMARY: Record<TrainingTrack, (intake: PlanIntake) => string> = {
  strength: (i) =>
    `### Your ${i.daysPerWeek}-day strength block\n\nYou train for **maximal strength**, so the week is built around heavy top sets (3–5 reps) with long rests, plus enough accessory volume to keep the joints honest. Squat, bench and deadlift each get one primary day.\n\n- Main lifts at RPE 8 with 2–3 reps in reserve.\n- A deload every fourth week: same movements, ~40% less volume.\n- Add 2.5 kg to the top set only when every rep is clean.`,
  hypertrophy: (i) =>
    `### Your ${i.daysPerWeek}-day hypertrophy block\n\nThe week is split by movement pattern so each muscle gets a second exposure. Weekly sets per muscle start at 14–18 and climb across the block.\n\n- Rep ranges 8–15 with the last set taken to RPE 9.\n- Supersets are marked in the plan — they save time without cutting volume.\n- Bodyweight is tracked weekly; aim for a slow, steady gain.`,
  conditioning: (i) =>
    `### Your ${i.daysPerWeek}-day indoor conditioning block\n\nEvery session happens in the gym: rower, ski erg, bike erg, sled and carries. No outdoor work is prescribed.\n\n- Intervals hold a 1:1 work:rest ratio; the plan lowers rest before it adds work.\n- Erg metres are logged, so progress is measured in output, not vibes.\n- One circuit day and one longer capacity day keep the engine broad.`,
  performance: (i) =>
    `### Your ${i.daysPerWeek}-day power block\n\nPower first, strength second: jumps, throws, plyometrics and sled accelerations are placed before any loaded work so quality stays high.\n\n- 3–5 reps per set, full rest, stop the set when height or distance drops.\n- Readiness is checked each session before load is added.\n- Landing quality is the gate — quiet landings before bigger boxes.`,
  wellness: (i) =>
    `### Your ${i.daysPerWeek}-day wellness & recovery block\n\nThis plan is about consistency and how you feel, not tonnage. Sessions are short, low-impact and designed to be done on a mat or with light resistance.\n\n- Mobility, stability and breathing work lead every session.\n- Pain is logged 0–10; anything at 4 or above changes the plan.\n- No heavy loading, no maximal efforts — the streak is the score.`,
  general: (i) =>
    `### Your ${i.daysPerWeek}-day general fitness block\n\nA balanced week: two full-body sessions and one shorter conditioning and core day. Enough structure to progress, little enough to keep.\n\n- Compound movements first, core and carries to finish.\n- RPE 6–7: you should finish each session feeling you could do another set.\n- Consistency beats intensity — three sessions a week is the target.`,
};

const TRACK_RATIONALE: Record<TrainingTrack, Array<{ rule: string; evidence: string }>> = {
  strength: [
    { rule: 'Primary lift 4 × 4 @ RPE 8', evidence: 'Heavy triples-to-fives with 2–3 reps in reserve drive maximal strength with manageable fatigue (RPE-based autoregulation).' },
    { rule: 'Weekly volume 14–16 sets per muscle', evidence: 'Enough to progress, low enough to recover between the three main lifts.' },
    { rule: 'Deload every 4th week', evidence: 'Fatigue accumulates on linear loading; a planned reduction keeps the top sets clean.' },
    { rule: 'Lower-back injury flag respected', evidence: 'Hinge volume capped and Romanian deadlifts substituted for good mornings.' },
  ],
  hypertrophy: [
    { rule: 'Weekly sets per muscle 14–18, climbing', evidence: 'Dose–response for hypertrophy plateaus around 20 sets; the ramp avoids junk volume.' },
    { rule: 'Rep ranges 8–15 to RPE 8–9', evidence: 'Equivalent hypertrophy to heavier loading with less joint stress when effort is matched.' },
    { rule: 'Supersets for arms and shoulders', evidence: 'Same volume in less time; no measurable interference for small muscle groups.' },
    { rule: 'Bodyweight tracked weekly', evidence: 'A slow gain supports muscle growth without unnecessary fat gain.' },
  ],
  conditioning: [
    { rule: 'Work:rest held at 1:1', evidence: 'Interval density is the strongest driver of aerobic adaptation indoors; rest is reduced before work is added.' },
    { rule: 'Erg metres logged every interval block', evidence: 'Output in metres/watts is the objective measure of engine progress.' },
    { rule: 'One long capacity day per week', evidence: 'Extends time at threshold without adding impact or outdoor sessions.' },
    { rule: 'Sled and carry work after intervals', evidence: 'Strength-endurance without the eccentric load that would blunt the next interval day.' },
  ],
  performance: [
    { rule: 'Power movements before loaded work', evidence: 'Explosive quality drops sharply with fatigue; ordering protects it.' },
    { rule: '1–5 reps with full rest', evidence: 'Maximal intent with incomplete recovery trains power, not endurance.' },
    { rule: 'Readiness gate before load is added', evidence: 'Readiness below 80% triggers a volume reduction rather than a missed session.' },
    { rule: 'Box height and throw distance tracked', evidence: 'Power markers improve before 1RM does; they are the early signal.' },
  ],
  wellness: [
    { rule: 'Mobility and stability lead every session', evidence: 'For recovery-focused clients, range and control are the outcome being trained.' },
    { rule: 'Pain score 0–10 logged each session', evidence: 'Pain at or above 4 changes the plan — loading is never pushed through it.' },
    { rule: 'No maximal or heavy loading', evidence: 'The goal is consistency and symptom management, not strength peaks.' },
    { rule: 'Short sessions, high frequency', evidence: 'Daily gentle work beats two hard sessions for adherence and symptom relief.' },
  ],
  general: [
    { rule: 'Two full-body sessions plus one conditioning day', evidence: 'Full-body sessions hit each pattern twice a week at a manageable time cost.' },
    { rule: 'RPE 6–7 across the block', evidence: 'Leaves room to recover and keeps adherence high for newer lifters.' },
    { rule: 'Compound movements first', evidence: 'Highest-value work is done while fresh; isolation fills the remaining time.' },
    { rule: 'Consistency is the tracked metric', evidence: 'Sessions per week predicts progress better than any single session.' },
  ],
};

export function demoGeneratedPlan(intake?: PlanIntake): GeneratedPlan {
  const track: TrainingTrack = intake?.track ?? currentTrack;
  const meta = trackMeta(track);
  const daysPerWeek = intake?.daysPerWeek ?? 4;
  const templates = TRACK_DAYS[track];
  const days = Array.from({ length: daysPerWeek }, (_, index) => {
    const template = templates[index % templates.length]!;
    return {
      dayIndex: index + 1,
      label: template.label,
      focus: template.focus,
      exercises: template.exercises.map((exercise) => ({ ...exercise })),
    };
  });
  planCounter += 1;
  const safeIntake: PlanIntake =
    intake ??
    ({
      goal: 'general',
      track,
      experience: 'intermediate',
      daysPerWeek: 4,
      sessionMinutes: 60,
      heightCm: 178,
      bodyweightKg: 82.4,
      age: 34,
      sex: 'unspecified',
      equipment: ['barbell', 'dumbbell', 'machine', 'cable', 'bodyweight', 'kettlebell', 'band'],
      injuryFlags: [],
    } satisfies PlanIntake);

  return {
    planId: `plan-${track}-${planCounter}`,
    name: `${meta.label} · ${daysPerWeek}-day block`,
    goal: safeIntake.goal,
    track,
    trackLabel: meta.label,
    weeks: 8,
    daysPerWeek,
    summaryMd: TRACK_SUMMARY[track](safeIntake),
    rationale: TRACK_RATIONALE[track],
    days,
  };
}

export function demoProgramme(): GeneratedPlan {
  return demoGeneratedPlan({
    goal: currentTrack === 'strength' ? 'strength' : currentTrack === 'hypertrophy' ? 'hypertrophy' : 'general',
    track: currentTrack,
    experience: 'intermediate',
    daysPerWeek: 4,
    sessionMinutes: 60,
    heightCm: 178,
    bodyweightKg: 82.4,
    age: 34,
    sex: 'unspecified',
    equipment: ['barbell', 'dumbbell', 'machine', 'cable', 'bodyweight', 'kettlebell', 'band'],
    injuryFlags: ['lower_back'],
    notes: 'Active programme',
  });
}

/* ---------------------------------------------------------------------------
   Sessions, sets and stats
   ------------------------------------------------------------------------ */

const TRACK_SESSION_LABEL: Record<TrainingTrack, string[]> = {
  strength: ['Lower · Squat', 'Upper · Press', 'Lower · Hinge', 'Upper · Volume'],
  hypertrophy: ['Push', 'Pull', 'Legs', 'Upper & arms'],
  conditioning: ['Engine intervals', 'Circuit', 'Capacity', 'Mixed ergs'],
  performance: ['Lower power', 'Upper power', 'Plyometrics', 'Power endurance'],
  wellness: ['Mobility flow', 'Stability', 'Low-impact strength', 'Recovery'],
  general: ['Full body A', 'Full body B', 'Conditioning & core', 'Full body A'],
};

export function demoSessions(): SessionSummary[] {
  const labels = TRACK_SESSION_LABEL[currentTrack];
  const demo = TRACK_DEMO[currentTrack];
  return labels.map((dayLabel, index) => ({
    sessionId: `s-demo-${index + 1}`,
    dayLabel,
    focus: [demo.dayFocus[index % demo.dayFocus.length] ?? 'FULL BODY'],
    completedAt: daysAgoIso(index * 2 + 1),
    volumeKg: Math.round(demo.volumeKg7 / Math.max(1, demo.completed)),
    sets: Math.round((demo.sets7 ?? 12) / Math.max(1, demo.completed)),
    prs: index === 0 ? (demo.prs7 ?? 0) : 0,
  }));
}

export function demoLogSet(set: LogSetInput): LogSetResult {
  const demo = TRACK_DEMO[currentTrack];
  const pr = set.weightKg > 100 && currentTrack !== 'wellness';
  return {
    saved: true,
    personalRecord: pr
      ? {
          exerciseName: set.exerciseName,
          weightKg: set.weightKg,
          reps: set.reps,
          estimated1rmKg: Math.round(set.weightKg * (1 + Math.min(set.reps, 12) / 30) * 100) / 100,
          achievedAt: new Date().toISOString(),
        }
      : null,
    sessionTotalKg: set.weightKg * set.reps,
    streakWeeks: demo.streak,
    coachHint: pr ? 'That is a new best — hold the same load next week before adding more.' : null,
  };
}

export function demoStats(): StatsSummary {
  const demo = TRACK_DEMO[currentTrack];
  return {
    track: currentTrack,
    sessions7: demo.completed,
    sessions28: demo.completed * 4,
    volumeKg28: demo.volumeKg7 * 4,
    sets28: (demo.sets7 ?? 0) * 4,
    prs28: (demo.prs7 ?? 0) * 2,
    intervalMinutes28: demo.metrics.intervalMinutes === null ? null : demo.metrics.intervalMinutes * 4,
    mobilitySessions28: demo.metrics.mobilitySessions === null ? null : demo.metrics.mobilitySessions * 4,
    avgPainScore: demo.metrics.painScore,
    consistencyPct: demo.compliancePct7 ?? 0,
  };
}

/** Weekday labels, exposed so screens can render the strip header consistently. */
export const WEEKDAY_LABELS = DAYS_OF_WEEK;

/* ---------------------------------------------------------------------------
   Blocks & goals — the 4-week block contract, as the fallback dataset.

   A block is four consecutive ISO weeks (Monday start). The seeded persona has
   a live block, four active goals and four finished blocks behind it; a
   brand-new signup (`u-demo-new`) has an empty block and no goals, so the
   screens can prove the honest empty state instead of rendering zeros as data.
   ------------------------------------------------------------------------ */

export const MAX_BLOCK_GOALS = 5;

/** Plain-English labels for the eight metrics — the only place they are named. */
export const GOAL_METRIC_LABELS: Record<GoalMetric, string> = {
  sessions: 'Sessions completed',
  sets: 'Working sets',
  volume_kg: 'Total volume (kg)',
  bodyweight_kg: 'Bodyweight (kg)',
  exercise_1rm: 'Best e1RM on an exercise',
  mobility_sessions: 'Mobility sessions',
  interval_minutes: 'Interval minutes',
  pain_average: 'Average pain (lower is better)',
};

/** Canonical units, used when a goal is created without an explicit unit. */
export const GOAL_METRIC_UNITS: Record<GoalMetric, string> = {
  sessions: 'sessions',
  sets: 'sets',
  volume_kg: 'kg',
  bodyweight_kg: 'kg',
  exercise_1rm: 'kg',
  mobility_sessions: 'sessions',
  interval_minutes: 'min',
  pain_average: '/10',
};

export const GOAL_METRICS: GoalMetric[] = [
  'sessions',
  'sets',
  'volume_kg',
  'bodyweight_kg',
  'exercise_1rm',
  'mobility_sessions',
  'interval_minutes',
  'pain_average',
];

/**
 * Which way "better" points for each metric. Every metric except pain counts
 * something you want more of; `pain_average` is a severity, so lower is better
 * and progress must be measured towards its ceiling, never as `current/target`.
 */
export const GOAL_METRIC_DIRECTIONS: Record<GoalMetric, 'up' | 'down'> = {
  sessions: 'up',
  sets: 'up',
  volume_kg: 'up',
  bodyweight_kg: 'up',
  exercise_1rm: 'up',
  mobility_sessions: 'up',
  interval_minutes: 'up',
  pain_average: 'down',
};

/** The one-line semantics shown under the metric picker. */
export const GOAL_METRIC_HELP: Record<GoalMetric, string> = {
  sessions: 'Completed sessions in this block. Higher is better.',
  sets: 'Working sets logged in this block (warm-ups excluded). Higher is better.',
  volume_kg: 'Weight × reps across every working set this block. Higher is better.',
  bodyweight_kg: 'Your latest recorded bodyweight. This one is a reading, not a score.',
  exercise_1rm: 'The best estimated 1RM for the exercise you pick, this block. Higher is better.',
  mobility_sessions: 'Completed wellness sessions this block. Higher is better.',
  interval_minutes: 'Minutes of conditioning work logged this block. Higher is better.',
  pain_average:
    'The average pain (0–10) you logged across this block’s working sets. Lower is better, so the target is a ceiling: set 2 for “average pain ≤ 2”. Progress is measured down towards that ceiling, never current ÷ target.',
};

/**
 * Direction-aware progress. For a lower-is-better metric (`pain_average`) the
 * naive `current / target` would invert the meaning — a worse average would
 * look like more progress. Here progress rises as the average falls towards the
 * ceiling, reaches 100 the moment it is at or below it, and stays 0 while there
 * is no evidence in the block (a goal is never achieved without a logged pain
 * score).
 */
export function goalProgressPct(
  metric: GoalMetric,
  current: number,
  target: number,
  hasData = true,
): number {
  if (!Number.isFinite(target) || target <= 0) return 0;
  if (GOAL_METRIC_DIRECTIONS[metric] === 'down') {
    if (!hasData) return 0;
    if (current <= target) return 100;
    return Math.min(100, Math.max(0, Math.round((target / current) * 100)));
  }
  return Math.min(999, Math.max(0, Math.round((current / target) * 100)));
}


/** The identity `demoAuthResponse` mints for a brand-new signup. */
export const FRESH_DEMO_USER_ID = 'u-demo-new';

let demoUserId: string = DEMO_LIFTER.id;

/** useSession tells the fallback dataset which demo account this tab reviews. */
export function setDemoUserId(userId: string | null): void {
  demoUserId = userId ?? DEMO_LIFTER.id;
}

/** A brand-new account has no log — empty blocks, no goals, no invented zeros. */
export function isFreshDemoAccount(): boolean {
  return demoUserId === FRESH_DEMO_USER_ID;
}

const BLOCK_WEEKS = 4;

/** The Monday the current block opened — two weeks back, so we are in week 3 of 4. */
function currentBlockStart(): string {
  return shiftIso(weekStarts(1)[0]!, -14);
}

function blockEnd(startIso: string): string {
  return shiftIso(startIso, BLOCK_WEEKS * 7 - 1);
}

interface DemoWeekSeed {
  sessions: number;
  target: number;
  sets: number;
  volumeKg: number;
}

const EMPTY_WEEK_SEEDS: DemoWeekSeed[] = Array.from({ length: BLOCK_WEEKS }, () => ({
  sessions: 0,
  target: 4,
  sets: 0,
  volumeKg: 0,
}));

const CURRENT_WEEK_INDEX = 3;

function weeksFrom(startIso: string, seeds: DemoWeekSeed[], currentWeekIndex: number): BlockWeek[] {
  return seeds.slice(0, BLOCK_WEEKS).map((seed, index) => ({
    weekStart: shiftIso(startIso, index * 7),
    sessions: seed.sessions,
    target: seed.target,
    sets: seed.sets,
    volumeKg: seed.volumeKg,
    adherencePct: seed.target > 0 ? Math.round((seed.sessions / seed.target) * 100) : 0,
    isCurrent: index + 1 === currentWeekIndex,
  }));
}

function totalsFrom(weeks: BlockWeek[]): BlockTotals {
  const sessions = weeks.reduce((sum, week) => sum + week.sessions, 0);
  const targetSessions = weeks.reduce((sum, week) => sum + week.target, 0);
  const sets = weeks.reduce((sum, week) => sum + week.sets, 0);
  const volumeKg = weeks.reduce((sum, week) => sum + week.volumeKg, 0);
  return {
    sessions,
    targetSessions,
    sets,
    volumeKg,
    activeDays: Math.min(BLOCK_WEEKS * 7, sessions),
    adherencePct: targetSessions > 0 ? Math.round((sessions / targetSessions) * 100) : 0,
  };
}

const CURRENT_WEEK_SEED: DemoWeekSeed[] = [
  { sessions: 4, target: 4, sets: 64, volumeKg: 18640 },
  { sessions: 3, target: 4, sets: 55, volumeKg: 16120 },
  { sessions: 3, target: 4, sets: 48, volumeKg: 13980 },
  { sessions: 0, target: 4, sets: 0, volumeKg: 0 },
];

function currentWeekSeeds(): DemoWeekSeed[] {
  return isFreshDemoAccount() ? EMPTY_WEEK_SEEDS : CURRENT_WEEK_SEED;
}

interface DemoGoalSeed {
  title: string;
  metric: GoalMetric;
  target: number;
  current: number;
  unit: string;
  exerciseId?: string;
}

const SEEDED_GOAL_SEEDS: DemoGoalSeed[] = [
  { title: 'Train four times a week', metric: 'sessions', target: 16, current: 10, unit: 'sessions' },
  {
    title: 'Squat 150 kg e1RM',
    metric: 'exercise_1rm',
    target: 150,
    current: 142.5,
    unit: 'kg',
    exerciseId: 'back-squat',
  },
  { title: 'Move 45,000 kg this block', metric: 'volume_kg', target: 45000, current: 48740, unit: 'kg' },
  { title: 'Eight mobility sessions', metric: 'mobility_sessions', target: 8, current: 4, unit: 'sessions' },
];

function toGoal(seed: DemoGoalSeed, index: number, dueOn: string): Goal {
  const progressPct = goalProgressPct(seed.metric, seed.current, seed.target);
  return {
    id: `g-demo-${index + 1}`,
    title: seed.title,
    metric: seed.metric,
    target: seed.target,
    current: seed.current,
    unit: seed.unit,
    progressPct,
    status: progressPct >= 100 ? 'achieved' : 'active',
    dueOn,
    exerciseId: seed.exerciseId ?? null,
  };
}

interface DemoGoalBag {
  seeded: Goal[];
  fresh: Goal[];
}

let demoGoalBag: DemoGoalBag | null = null;

function goalBag(): DemoGoalBag {
  if (!demoGoalBag) {
    const dueOn = blockEnd(currentBlockStart());
    demoGoalBag = { seeded: SEEDED_GOAL_SEEDS.map((seed, index) => toGoal(seed, index, dueOn)), fresh: [] };
  }
  return demoGoalBag;
}

function currentGoals(): Goal[] {
  return goalBag()[isFreshDemoAccount() ? 'fresh' : 'seeded'];
}

/** The current values a newly created demo goal would start from. */
function goalCurrentFor(metric: GoalMetric): number {
  if (isFreshDemoAccount()) return 0;
  const totals = totalsFrom(weeksFrom(currentBlockStart(), currentWeekSeeds(), CURRENT_WEEK_INDEX));
  const demo = TRACK_DEMO[currentTrack];
  switch (metric) {
    case 'sessions':
      return totals.sessions;
    case 'sets':
      return totals.sets;
    case 'volume_kg':
      return totals.volumeKg;
    case 'bodyweight_kg':
      return demo.metrics.bodyweightKg ?? 0;
    case 'exercise_1rm':
      return demo.metrics.bestE1rmKg ?? 0;
    case 'mobility_sessions':
      return demo.metrics.mobilitySessions ?? 0;
    case 'interval_minutes':
      return demo.metrics.intervalMinutes ?? 0;
    case 'pain_average':
      return demo.metrics.painScore ?? 0;
    default:
      return 0;
  }
}

/**
 * Whether the demo dataset actually holds a pain reading for this track. The
 * demo `painScore` is null on every track that does not focus on pain, and a
 * goal must not read "achieved" from an absent reading.
 */
function goalHasData(metric: GoalMetric): boolean {
  if (metric !== 'pain_average') return true;
  if (isFreshDemoAccount()) return false;
  return TRACK_DEMO[currentTrack].metrics.painScore !== null;
}

function goalFromInput(input: GoalInput): Goal {
  const current = goalCurrentFor(input.metric);
  const progressPct = goalProgressPct(input.metric, current, input.target, goalHasData(input.metric));
  return {
    id: uid('goal'),
    title: input.title.trim(),
    metric: input.metric,
    target: input.target,
    current,
    unit: input.unit ?? GOAL_METRIC_UNITS[input.metric],
    progressPct,
    status: progressPct >= 100 ? 'achieved' : 'active',
    dueOn: blockEnd(currentBlockStart()),
    exerciseId: input.metric === 'exercise_1rm' ? input.exerciseId ?? null : null,
  };
}

export function demoBlocksCurrent(): CurrentBlock {
  const start = currentBlockStart();
  const weeks = weeksFrom(start, currentWeekSeeds(), CURRENT_WEEK_INDEX);
  return {
    block: {
      index: 12,
      startsOn: start,
      endsOn: blockEnd(start),
      weekIndex: CURRENT_WEEK_INDEX,
      weeks: BLOCK_WEEKS,
    },
    totals: totalsFrom(weeks),
    weeks,
    goals: currentGoals().map((goal) => ({ ...goal })),
  };
}

/** Demo mutations stay in the tab (and in this module) — never a fake server. */
export function demoCreateGoal(input: GoalInput): Goal | null {
  const goals = currentGoals();
  if (goals.length >= MAX_BLOCK_GOALS) return null;
  const goal = goalFromInput(input);
  goals.push(goal);
  return { ...goal };
}

export function demoUpdateGoal(goalId: string, patch: GoalPatch): Goal | null {
  const goal = currentGoals().find((candidate) => candidate.id === goalId);
  if (!goal) return null;
  if (typeof patch.title === 'string' && patch.title.trim()) goal.title = patch.title.trim();
  if (typeof patch.target === 'number' && Number.isFinite(patch.target) && patch.target > 0) {
    goal.target = patch.target;
  }
  if (patch.status) goal.status = patch.status;
  goal.progressPct = goalProgressPct(goal.metric, goal.current, goal.target, goalHasData(goal.metric));
  if (goal.progressPct >= 100 && goal.status !== 'missed') goal.status = 'achieved';
  return { ...goal };
}

export function demoDeleteGoal(goalId: string): boolean {
  const goals = currentGoals();
  const index = goals.findIndex((goal) => goal.id === goalId);
  if (index < 0) return false;
  goals.splice(index, 1);
  return true;
}

interface DemoPastBlockSeed {
  index: number;
  goalsMet: number;
  goalsTotal: number;
  weeks: DemoWeekSeed[];
}

const PAST_BLOCK_SEEDS: DemoPastBlockSeed[] = [
  {
    index: 11,
    goalsMet: 3,
    goalsTotal: 3,
    weeks: [
      { sessions: 4, target: 4, sets: 62, volumeKg: 17900 },
      { sessions: 4, target: 4, sets: 66, volumeKg: 19250 },
      { sessions: 3, target: 4, sets: 51, volumeKg: 15300 },
      { sessions: 4, target: 4, sets: 60, volumeKg: 17400 },
    ],
  },
  {
    index: 10,
    goalsMet: 2,
    goalsTotal: 3,
    weeks: [
      { sessions: 3, target: 4, sets: 49, volumeKg: 14800 },
      { sessions: 4, target: 4, sets: 58, volumeKg: 16900 },
      { sessions: 2, target: 4, sets: 33, volumeKg: 9900 },
      { sessions: 4, target: 4, sets: 61, volumeKg: 17650 },
    ],
  },
  {
    index: 9,
    goalsMet: 1,
    goalsTotal: 2,
    weeks: [
      { sessions: 2, target: 4, sets: 31, volumeKg: 9200 },
      { sessions: 3, target: 4, sets: 45, volumeKg: 13400 },
      { sessions: 3, target: 4, sets: 47, volumeKg: 13950 },
      { sessions: 3, target: 4, sets: 44, volumeKg: 13000 },
    ],
  },
  {
    index: 8,
    goalsMet: 2,
    goalsTotal: 2,
    weeks: [
      { sessions: 4, target: 4, sets: 59, volumeKg: 17100 },
      { sessions: 3, target: 4, sets: 46, volumeKg: 13350 },
      { sessions: 4, target: 4, sets: 63, volumeKg: 18200 },
      { sessions: 4, target: 4, sets: 65, volumeKg: 18850 },
    ],
  },
];

export function demoBlocksHistory(limit = 6): BlockHistoryPayload {
  if (isFreshDemoAccount()) return { blocks: [] };
  const start = currentBlockStart();
  const blocks: BlockHistoryEntry[] = PAST_BLOCK_SEEDS.slice(0, limit).map((seed, offset) => {
    const blockStart = shiftIso(start, -(offset + 1) * BLOCK_WEEKS * 7);
    const weeks = weeksFrom(blockStart, seed.weeks, 0);
    const totals = totalsFrom(weeks);
    return {
      index: seed.index,
      startsOn: blockStart,
      endsOn: blockEnd(blockStart),
      sessions: totals.sessions,
      targetSessions: totals.targetSessions,
      sets: totals.sets,
      volumeKg: totals.volumeKg,
      adherencePct: totals.adherencePct,
      goalsMet: seed.goalsMet,
      goalsTotal: seed.goalsTotal,
      weeks,
    };
  });
  return { blocks };
}

interface DemoSetSeed {
  movement: string;
  variant?: string;
  weightKg: number | null;
  reps: number;
  rpe: number | null;
}

const RECENT_SET_SEEDS: Record<TrainingTrack, DemoSetSeed[]> = {
  strength: [
    { movement: 'Back Squat', variant: 'High bar', weightKg: 140, reps: 3, rpe: 8 },
    { movement: 'Bench Press', variant: 'Competition', weightKg: 102.5, reps: 5, rpe: 8 },
    { movement: 'Deadlift', variant: 'Conventional', weightKg: 180, reps: 3, rpe: 9 },
    { movement: 'Overhead Press', weightKg: 62.5, reps: 6, rpe: 8 },
    { movement: 'Barbell Row', variant: 'Pendlay', weightKg: 95, reps: 8, rpe: 7.5 },
    { movement: 'Romanian Deadlift', weightKg: 120, reps: 8, rpe: 7 },
  ],
  hypertrophy: [
    { movement: 'Incline Dumbbell Press', variant: '30°', weightKg: 32.5, reps: 10, rpe: 8 },
    { movement: 'Lat Pulldown', variant: 'Wide grip', weightKg: 70, reps: 10, rpe: 8 },
    { movement: 'Leg Press', weightKg: 160, reps: 12, rpe: 8 },
    { movement: 'Cable Lateral Raise', weightKg: 12.5, reps: 15, rpe: 9 },
    { movement: 'Chest-Supported Row', weightKg: 55, reps: 10, rpe: 8 },
    { movement: 'Machine Preacher Curl', weightKg: 30, reps: 12, rpe: 9 },
  ],
  conditioning: [
    { movement: 'Rower', variant: '6 × 500 m', weightKg: null, reps: 500, rpe: 8 },
    { movement: 'Ski Erg', variant: '10 × 1 min', weightKg: null, reps: 60, rpe: 8 },
    { movement: 'Sled Push', variant: 'Heavy', weightKg: 92.5, reps: 20, rpe: 8 },
    { movement: 'Bike Erg', variant: '3 × 8 min', weightKg: null, reps: 480, rpe: 7 },
    { movement: 'Farmer Carry', weightKg: 65, reps: 40, rpe: 8 },
    { movement: 'Kettlebell Swing', variant: 'Russian', weightKg: 24, reps: 15, rpe: 7 },
  ],
  performance: [
    { movement: 'Box Jump', variant: 'Low box', weightKg: null, reps: 3, rpe: 6 },
    { movement: 'Trap Bar Jump', weightKg: 62.5, reps: 3, rpe: 7 },
    { movement: 'Med Ball Chest Throw', variant: '4 kg', weightKg: 4, reps: 4, rpe: 7 },
    { movement: 'Back Squat', variant: 'Speed', weightKg: 100, reps: 3, rpe: 7 },
    { movement: 'Sled Acceleration', variant: '6 × 15 m', weightKg: 40, reps: 15, rpe: 8 },
    { movement: 'Plyo Push-up', weightKg: null, reps: 5, rpe: 6 },
  ],
  wellness: [
    { movement: 'Mat Mobility', variant: 'Full body', weightKg: null, reps: 20, rpe: 2 },
    { movement: 'Dead Bug', weightKg: null, reps: 10, rpe: 4 },
    { movement: '90/90 Hip Switch', weightKg: null, reps: 8, rpe: 3 },
    { movement: 'Glute Bridge', weightKg: null, reps: 12, rpe: 4 },
    { movement: 'Side Plank', weightKg: null, reps: 30, rpe: 4 },
    { movement: 'Thoracic Rotation', variant: 'Open book', weightKg: null, reps: 8, rpe: 3 },
  ],
  general: [
    { movement: 'Goblet Squat', weightKg: 24, reps: 10, rpe: 6 },
    { movement: 'Dumbbell Bench Press', weightKg: 22.5, reps: 10, rpe: 6 },
    { movement: 'Lat Pulldown', weightKg: 55, reps: 12, rpe: 6 },
    { movement: 'Romanian Deadlift', weightKg: 60, reps: 10, rpe: 6 },
    { movement: 'Cable Row', variant: 'Seated', weightKg: 50, reps: 12, rpe: 6 },
    { movement: 'Farmer Carry', weightKg: 48, reps: 40, rpe: 6 },
  ],
};

export function demoRecentSets(limit = 6): RecentSet[] {
  if (isFreshDemoAccount()) return [];
  const labels = TRACK_SESSION_LABEL[currentTrack];
  return RECENT_SET_SEEDS[currentTrack].slice(0, limit).map((seed, index) => ({
    id: `recent-${currentTrack}-${index + 1}`,
    movement: seed.movement,
    variant: seed.variant ?? null,
    weightKg: seed.weightKg,
    reps: seed.reps,
    rpe: seed.rpe,
    performedAt: daysAgoIso(Math.floor(index / 2)),
    sessionLabel: labels[index % labels.length] ?? 'Session',
  }));
}

const DEMO_EXERCISES: ExerciseOption[] = [
  { id: 'back-squat', slug: 'back-squat', name: 'Back Squat' },
  { id: 'bench-press', slug: 'bench-press', name: 'Bench Press' },
  { id: 'deadlift', slug: 'deadlift', name: 'Deadlift' },
  { id: 'overhead-press', slug: 'overhead-press', name: 'Overhead Press' },
  { id: 'barbell-row', slug: 'barbell-row', name: 'Barbell Row' },
  { id: 'romanian-deadlift', slug: 'romanian-deadlift', name: 'Romanian Deadlift' },
  { id: 'front-squat', slug: 'front-squat', name: 'Front Squat' },
  { id: 'weighted-pull-up', slug: 'weighted-pull-up', name: 'Weighted Pull-up' },
  { id: 'incline-dumbbell-press', slug: 'incline-dumbbell-press', name: 'Incline Dumbbell Press' },
  { id: 'trap-bar-deadlift', slug: 'trap-bar-deadlift', name: 'Trap Bar Deadlift' },
];

export function demoExercises(): ExerciseOption[] {
  return DEMO_EXERCISES.map((exercise) => ({ ...exercise }));
}

/* ---------------------------------------------------------------------------
   The lifter's session schedule — the offline instance of the "when" layer.
   The demo persona has a Monday/Thursday week set on their own block, so the
   dashboard card shows the "your own schedule" state and the editor has
   something real to edit. Writes mutate this in-memory week, exactly as the
   other demo write helpers do.
   ------------------------------------------------------------------------ */

const DEMO_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function demoLabelFor(weekday: number, startMinute: number | null): string {
  const base = DEMO_WEEKDAYS[(weekday - 1 + 7) % 7] ?? 'Monday';
  if (startMinute === null) return base;
  const hour = String(Math.floor(startMinute / 60)).padStart(2, '0');
  const minute = String(startMinute % 60).padStart(2, '0');
  return `${base} · ${hour}:${minute}`;
}

/** The next date (from today) that falls on an ISO weekday. */
function demoNextDate(weekday: number): Date {
  const now = new Date();
  const current = now.getDay() === 0 ? 7 : now.getDay();
  const delta = (weekday - current + 7) % 7;
  const next = new Date(now);
  next.setDate(now.getDate() + delta);
  return next;
}

function buildDemoSchedule(): LifterSchedule {
  const days = [
    {
      planDayId: 'demo-day-upper-a',
      planId: 'demo-plan',
      planName: 'Strength',
      coachOwned: false,
      dayIndex: 1,
      label: 'Upper A',
      focus: ['Push', 'Pull'],
      entryId: 'demo-entry-upper-a',
      weekday: 1,
      startMinute: 1080,
    },
    {
      planDayId: 'demo-day-lower-a',
      planId: 'demo-plan',
      planName: 'Strength',
      coachOwned: false,
      dayIndex: 2,
      label: 'Lower A',
      focus: ['Squat'],
      entryId: 'demo-entry-lower-a',
      weekday: 4,
      startMinute: 1080,
    },
    {
      planDayId: 'demo-day-upper-b',
      planId: 'demo-plan',
      planName: 'Strength',
      coachOwned: false,
      dayIndex: 3,
      label: 'Upper B',
      focus: ['Press'],
      entryId: null,
      weekday: null,
      startMinute: null,
    },
    {
      planDayId: 'demo-day-lower-b',
      planId: 'demo-plan',
      planName: 'Strength',
      coachOwned: false,
      dayIndex: 4,
      label: 'Lower B',
      focus: ['Hinge'],
      entryId: null,
      weekday: null,
      startMinute: null,
    },
  ];
  const entries: ScheduleEntry[] = days
    .filter((day) => day.entryId !== null)
    .map((day) => ({
      entryId: day.entryId as string,
      planDayId: day.planDayId,
      planId: day.planId,
      planName: day.planName,
      coachOwned: day.coachOwned,
      dayIndex: day.dayIndex,
      dayLabel: day.label,
      focus: day.focus,
      weekday: day.weekday as number,
      startMinute: day.startMinute,
      repeatWeekly: true,
      repeatUntil: null,
      stoppedAt: null,
      nextStartsAt: demoNextDate(day.weekday as number).toISOString(),
    }));
  return { timezone: 'Europe/London', active: true, repeatUntil: null, entries, days };
}

let demoScheduleState: LifterSchedule | null = null;

export function demoSchedule(): LifterSchedule {
  demoScheduleState ??= buildDemoSchedule();
  return demoScheduleState;
}

/** Offline "save my week": replace the entries, mirroring the API's PUT. */
export function demoSaveSchedule(input: ScheduleInput): LifterSchedule {
  const current = demoSchedule();
  const byDay = new Map(current.days.map((day) => [day.planDayId, day]));
  const days = current.days.map((day) => {
    const chosen = input.days.find((entry) => entry.planDayId === day.planDayId);
    return {
      ...day,
      entryId: chosen ? `demo-entry-${day.planDayId}` : null,
      weekday: chosen ? chosen.weekday : null,
      startMinute: chosen ? chosen.startMinute : null,
    };
  });
  const entries: Array<ScheduleEntry | null> = input.days
    .map((chosen) => {
      const day = byDay.get(chosen.planDayId);
      if (!day) return null;
      return {
        entryId: `demo-entry-${day.planDayId}`,
        planDayId: day.planDayId,
        planId: day.planId,
        planName: day.planName,
        coachOwned: day.coachOwned,
        dayIndex: day.dayIndex,
        dayLabel: day.label,
        focus: day.focus,
        weekday: chosen.weekday,
        startMinute: chosen.startMinute,
        repeatWeekly: true,
        repeatUntil: input.repeatUntil,
        stoppedAt: null,
        nextStartsAt: demoNextDate(chosen.weekday).toISOString(),
      };
    });
  const live = entries.filter((entry): entry is ScheduleEntry => entry !== null);
  demoScheduleState = {
    timezone: current.timezone,
    active: live.length > 0,
    repeatUntil: input.repeatUntil,
    entries: live,
    days,
  };
  return demoScheduleState;
}

export function demoStopSchedule(): LifterSchedule {
  const current = demoSchedule();
  demoScheduleState = {
    ...current,
    active: false,
    entries: current.entries.map((entry) => ({
      ...entry,
      repeatWeekly: false,
      stoppedAt: new Date().toISOString(),
      nextStartsAt: null,
    })),
    days: current.days.map((day) => ({ ...day, entryId: null, weekday: null, startMinute: null })),
  };
  return demoScheduleState;
}

export function demoClearSchedule(): LifterSchedule {
  const current = demoSchedule();
  demoScheduleState = {
    ...current,
    active: false,
    repeatUntil: null,
    entries: [],
    days: current.days.map((day) => ({ ...day, entryId: null, weekday: null, startMinute: null })),
  };
  return demoScheduleState;
}

export function demoSkipScheduleEntry(entryId: string): LifterSchedule {
  const current = demoSchedule();
  demoScheduleState = {
    ...current,
    entries: current.entries.map((entry) =>
      entry.entryId === entryId
        ? { ...entry, nextStartsAt: new Date(new Date(entry.nextStartsAt ?? Date.now()).getTime() + 7 * 86_400_000).toISOString() }
        : entry,
    ),
  };
  return demoScheduleState;
}

/**
 * The offline reminder shown on the dashboard card: the next of the demo week,
 * so the "your own schedule" state is the one reviewers see without an API.
 */
export function demoNextSession(): NextSession {
  const week = demoSchedule();
  const next = week.entries
    .filter((entry) => entry.nextStartsAt !== null)
    .sort((a, b) => Date.parse(a.nextStartsAt ?? '') - Date.parse(b.nextStartsAt ?? ''))[0];
  if (!next || next.nextStartsAt === null) {
    return { kind: 'none', startsAt: null, hasTime: false, dayLabel: null, planName: null, source: 'none' };
  }
  return {
    kind: 'scheduled',
    startsAt: next.nextStartsAt,
    hasTime: next.startMinute !== null,
    dayLabel: demoLabelFor(next.weekday, next.startMinute),
    planName: next.planName,
    source: 'schedule',
    scheduleId: next.entryId,
  };
}

